import {
  type FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Link, newAsyncDataEnvelope, time } from '@dr.pogodin/react-utils';

import VideoThumbnail from 'components/VideoThumbnail';
import { LaunchExperienceButton } from 'components/ai-app';

import { useArtists, useExperience } from 'hooks';

import {
  E_EXPERIENCE,
  type ExperienceT,
  type SessionT,
  getId,
  useGlobalStateObject,
} from 'utils';

import videoThumbnailTheme from './video-thumbnail.scss';

import './style.scss';

type PropsT = {
  experience: ExperienceT;
  session: SessionT;
};

/** Returns intent tags covered by videos in the experience, most-common
 *  first, mirroring how `aggregateBodyAreaCategories()` ranks body areas. */
function aggregateIntents(experience: ExperienceT): string[] {
  const counter: Record<string, number> = {};
  for (const { intent } of experience.videos) {
    if (intent) counter[intent] = (counter[intent] ?? 0) + 1;
  }
  return Object.keys(counter).sort(
    (a, b) => (counter[b] ?? 0) - (counter[a] ?? 0),
  );
}

const Item: FunctionComponent<PropsT> = ({ experience, session }) => {
  const gs = useGlobalStateObject();

  const artistIds = [...new Set(experience.videos.map((v) => v.artistId))];
  const artists = useArtists(artistIds);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onOutsideClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };

    document.addEventListener('mousedown', onOutsideClick);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
    };
  }, [menuOpen]);

  let durationMin = 0;
  for (const video of experience.videos) durationMin += video.durationMin ?? 0;

  const visibleIntents = aggregateIntents(experience);

  const dateTimeLabel = session.startedAt
    ? time(session.startedAt).format('MMM D, YYYY • h:mm A')
    : null;

  const e = useExperience();

  const repeatButton = (
    <LaunchExperienceButton
      allowUnprepared
      checkInId={null}
      experience={experience}
      sessionKind={null}
      setExperience={(experienceData) => {
        e.set({
          checkInId: null,
          experience: experienceData,
          sessionKind: null,
        });
      }}
      title="Return"
    />
  );

  return (
    <div styleName="card">
      <div styleName="header">
        <div styleName="dateTime">{dateTimeLabel}</div>
        <div styleName="totalDuration">{Math.round(durationMin)} min</div>
        {repeatButton}
      </div>

      <div styleName="title">{experience.title}</div>

      {visibleIntents.length ? (
        <div styleName="tags">
          {visibleIntents.map((intent) => <span key={intent} styleName="tagPill">{intent}</span>)}
        </div>
      ) : null}

      <div styleName="desktopHeader">
        <div styleName="desktopHeaderMain">
          <div styleName="dateTime">{dateTimeLabel}</div>
          <div styleName="desktopTitle">{experience.title}</div>
          {visibleIntents.length ? (
            <div styleName="tagPills">
              {visibleIntents.map((intent) => <span key={intent} styleName="tagPill">{intent}</span>)}
            </div>
          ) : null}
        </div>
        <div styleName="desktopHeaderSide">
          <div ref={menuRef} styleName="menuWrap">
            <button
              aria-label="Session options"
              onClick={() => {
                setMenuOpen((prev) => !prev);
              }}
              styleName="menuButton"
              type="button"
            >
              <span />
              <span />
              <span />
            </button>
            {menuOpen ? (
              <div styleName="menu">
                <Link
                  onClick={() => {
                    gs.set('experience', newAsyncDataEnvelope({
                      checkInId: null,
                      experience,
                      sessionKind: null,
                    }, {
                      timestamp: Date.now(),
                    }));
                  }}
                  styleName="menuItem"
                  to={`${E_EXPERIENCE}?edit=1`}
                >
                  Edit
                </Link>
                <Link styleName="menuItem" to="">
                  Delete
                </Link>
              </div>
            ) : null}
          </div>
          <div styleName="stats">
            {repeatButton}
            <div styleName="stat">
              <div styleName="statLabel">Session</div>
              <div styleName="statValue statValueAccent">
                {experience.videos.length}
              </div>
            </div>
            <div styleName="stat">
              <div styleName="statLabel">Total Duration</div>
              <div styleName="statValue">{Math.round(durationMin)} min</div>

            </div>
          </div>
        </div>
      </div>

      <div styleName="divider" />

      <div styleName="videos">
        {experience.videos.map((video, idx) => {
          const artist = artists.items[video.artistId]?.data;
          const artistName = artist
            ? `${artist.user.firstName} ${artist.user.lastName}`.trim()
            : '';

          return (
            // NOTE: The key includes the index for the case if the same video
            // is present in the experience multiple times.
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${idx}/${getId(video)}`} styleName="videoRow">
              <VideoThumbnail
                imageUrl={video.thumbnailUrl}
                removed={video.status !== 'Published'}
                theme={videoThumbnailTheme}
              />
              <div styleName="videoInfo">
                <div styleName="videoTitle">{video.title}</div>
                <div styleName="videoMeta">
                  {Math.round(video.durationMin ?? 0)} Minutes
                  {artistName ? ` · ${artistName}` : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Item;
